import {useEffect, useState} from 'react';
import {CircularProgress, Link, List, ListItem, ListItemText, Typography} from '@mui/material';
import {Show, SimpleShowLayout, fetchUtils, useRecordContext, useResourceContext, useShowContext, useTranslate} from 'react-admin';
import inMemoryJWT from '../../inMemoryJWT';

type PdfHistoryItem = {
  filename: string;
  url: string;
  moduleType: 'technicianRiskAssessment' | 'technicianWorkReport';
  generatedAt: string;
};

const OperationDocumentHistory = () => {
  const {record} = useShowContext();
  const hasWorkPermit = record?.workPermit?.status === 'done';
  const t = useTranslate();
  const [items, setItems] = useState<PdfHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPdfHistory = async () => {
      if (!record?.id || !hasWorkPermit || !process.env.REACT_APP_API_URL) {
        setItems([]);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const headers = new Headers({Accept: 'application/json'});
        const token = inMemoryJWT.getToken();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }

        const {json} = await fetchUtils.fetchJson(`${process.env.REACT_APP_API_URL}/operations/${record.id}/pdf-history`, {
          headers,
        });

        if (isMounted) {
          setItems(Array.isArray(json) ? json : []);
        }
      } catch (exception) {
        if (isMounted) {
          setItems([]);
          setError(t('ra.message.http_error'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchPdfHistory();

    return () => {
      isMounted = false;
    };
  }, [record?.id, hasWorkPermit, t]);

  const getModuleLabel = (moduleType: PdfHistoryItem['moduleType']) => {
    return t(`resources.operations.fields.${moduleType}`);
  };

  const formatGeneratedAt = (value: string) => new Date(value).toLocaleString('sv-SE');

  return (
    <div>
      <Typography variant="h6">{t('resources.operations.fields.pdfHistory')}</Typography>
      {!hasWorkPermit ? <Typography variant="body2">{t('resources.operations.fields.pendingWorkPermit')}</Typography> : null}
      {hasWorkPermit && isLoading ? <CircularProgress size={18} /> : null}
      {hasWorkPermit && error ? <Typography variant="body2">{error}</Typography> : null}
      {hasWorkPermit && !isLoading && !error && items.length === 0 ? (
        <Typography variant="body2">{t('resources.operations.fields.pendingDocument')}</Typography>
      ) : null}
      {hasWorkPermit && !isLoading && !error && items.length > 0 ? (
        <List dense>
          {items.map(item => (
            <ListItem key={`${item.moduleType}-${item.filename}`} disableGutters>
              <ListItemText
                primary={
                  <Link href={item.url} target="_blank" rel="noreferrer">
                    {item.filename}
                  </Link>
                }
                secondary={`${getModuleLabel(item.moduleType)} - ${formatGeneratedAt(item.generatedAt)}`}
              />
            </ListItem>
          ))}
        </List>
      ) : null}
    </div>
  );
};

export const OperationShow = () => {
  const resource = useResourceContext();
  const record = useRecordContext();

  return (
    <Show
      resource={resource}
      id={record?.id}
      children={
        <SimpleShowLayout
          children={
            <>
              <OperationDocumentHistory />
            </>
          }
        />
      }
    />
  );
};
